import { create } from 'zustand';
import {
  getPods,
  getPodDetail,
  createPod,
  renamePod,
  deletePod,
  invitePodMember,
  leavePod,
  removePodMember,
  acceptPodInvite,
} from '../services/podsApi';
import type { PodListItem, PodDetail } from '../services/podsApi';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

interface PodsState {
  pods: PodListItem[];
  activePod: PodDetail | null;
  isLoading: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  fetchedAt: number | null;

  fetchPods: () => Promise<void>;
  fetchPodDetail: (id: string) => Promise<void>;
  createPod: (name: string, emails?: string[]) => Promise<PodListItem>;
  renamePod: (id: string, name: string) => Promise<void>;
  deletePod: (id: string) => Promise<void>;
  inviteMember: (podId: string, email: string) => Promise<void>;
  leavePod: (podId: string) => Promise<void>;
  removeMember: (podId: string, userId: number) => Promise<void>;
  acceptInvite: (podId: string) => Promise<void>;
  invalidate: () => void;
  clearError: () => void;
}

export const usePodsStore = create<PodsState>((set, get) => ({
  pods: [],
  activePod: null,
  isLoading: false,
  isLoadingDetail: false,
  error: null,
  fetchedAt: null,

  fetchPods: async () => {
    const { fetchedAt } = get();
    if (fetchedAt && Date.now() - fetchedAt < STALE_TIME) return;
    set({ isLoading: true, error: null });
    try {
      const pods = await getPods();
      set({ pods, isLoading: false, fetchedAt: Date.now() });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load Pods',
      });
    }
  },

  fetchPodDetail: async (id: string) => {
    set({ isLoadingDetail: true, error: null });
    try {
      const pod = await getPodDetail(id);
      set({ activePod: pod, isLoadingDetail: false });
    } catch (err) {
      set({
        isLoadingDetail: false,
        error: err instanceof Error ? err.message : 'Failed to load Pod',
      });
    }
  },

  createPod: async (name: string, emails?: string[]) => {
    const pod = await createPod(name, emails);
    set(s => ({ pods: [pod, ...s.pods], fetchedAt: null }));
    return pod;
  },

  renamePod: async (id: string, name: string) => {
    const updated = await renamePod(id, name);
    set(s => ({
      activePod: s.activePod?.id === id ? updated : s.activePod,
      pods: s.pods.map(p => p.id === id ? { ...p, name } : p),
    }));
  },

  deletePod: async (id: string) => {
    await deletePod(id);
    set(s => ({
      pods: s.pods.filter(p => p.id !== id),
      activePod: s.activePod?.id === id ? null : s.activePod,
    }));
  },

  inviteMember: async (podId: string, email: string) => {
    await invitePodMember(podId, email);
    // Refresh detail to show new pending member
    const pod = await getPodDetail(podId);
    set({ activePod: pod, fetchedAt: null });
  },

  leavePod: async (podId: string) => {
    await leavePod(podId);
    set(s => ({
      pods: s.pods.filter(p => p.id !== podId),
      activePod: s.activePod?.id === podId ? null : s.activePod,
    }));
  },

  removeMember: async (podId: string, userId: number) => {
    await removePodMember(podId, userId);
    const pod = await getPodDetail(podId);
    set({ activePod: pod, fetchedAt: null });
  },

  acceptInvite: async (podId: string) => {
    await acceptPodInvite(podId);
    set({ fetchedAt: null }); // Force re-fetch to get full detail access
  },

  invalidate: () => set({ fetchedAt: null }),
  clearError: () => set({ error: null }),
}));

// Selector hooks for granular subscriptions
export const usePods = () => usePodsStore(s => s.pods);
export const useActivePod = () => usePodsStore(s => s.activePod);
export const usePodsLoading = () => usePodsStore(s => s.isLoading);
